
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Service } from "@/types/service";

export const useAdminServices = (selectedRegion: string) => {
  return useQuery({
    queryKey: ["admin-services", selectedRegion],
    queryFn: async () => {
      console.log("Fetching admin services for region:", selectedRegion);
      
      // First, get services with subcategory details
      let servicesQuery = supabase
        .from("services")
        .select(`
          *,
          subcategory_details:subcategories(
            id, 
            label,
            parent:categories(id, label)
          )
        `);

      // Only filter by region if selectedRegion is provided and not empty
      if (selectedRegion && selectedRegion.trim() !== '') {
        servicesQuery = servicesQuery.eq('region', selectedRegion);
      }

      const { data: services, error: servicesError } = await servicesQuery
        .order('test_only', { ascending: true }) // Show production services first
        .order('featured', { ascending: false })
        .order('display_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (servicesError) {
        console.error("Error fetching services:", servicesError);
        throw servicesError;
      }

      if (!services || services.length === 0) {
        console.log("No services found");
        return [];
      }

      // Get unique owner IDs
      const ownerIds = [...new Set(services
        .map(service => service.owner_id)
        .filter(Boolean)
      )];

      // Fetch owner profiles separately if there are any
      let ownerProfiles: any[] = [];
      if (ownerIds.length > 0) {
        const { data: profiles, error: profilesError } = await supabase
          .from("profiles")
          .select("id, display_name, email")
          .in("id", ownerIds);

        if (profilesError) {
          console.error("Error fetching owner profiles:", profilesError);
        } else {
          ownerProfiles = profiles || [];
        }
      }

      // Map owner profiles to services
      const servicesWithOwners = services.map(service => ({
        ...service,
        owner: service.owner_id 
          ? ownerProfiles.find(profile => profile.id === service.owner_id) || null
          : null
      }));

      console.log("Fetched services with owners:", servicesWithOwners.length);
      
      return servicesWithOwners as Service[];
    },
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });
};
